import { Component, OnInit, OnDestroy } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { ToastrService } from 'ngx-toastr';
import { EnquiryRequest } from '../../../models/enquiry.model';
import { Subject, forkJoin, takeUntil } from 'rxjs';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  selectedCategory = 0;

  categories: any[] = [];
  products: any[] = [];
  filteredProducts: any[] = [];
  isLoading = true;

  // Enquiry modal
  isEnquiryModalOpen = false;
  enquiryProductId: number = 0;
  enquiryProductName: string = '';
  enquiryForm: EnquiryRequest = this.getEmptyEnquiry();
  isSubmitting = false;

  aboutFeatures = [
    {
      icon: 'fas fa-industry',
      title: 'Tool Room Machinery Sales',
      description: 'Wide range of precision tool room machines from trusted brands'
    },
    {
      icon: 'fas fa-wrench',
      title: 'Installation & Commissioning',
      description: 'End-to-end setup, alignment, and commissioning support'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Preventive Maintenance',
      description: 'Scheduled servicing to keep your machines running at peak performance'
    },
    {
      icon: 'fas fa-headset',
      title: 'Breakdown & On-site Support',
      description: 'Rapid response service to minimize production downtime'
    }
  ];

  whyChooseUs = [
    {
      icon: 'fas fa-medal',
      title: 'Reliable & Quality-Assured',
      description: 'Every machine meets strict performance and durability standards.'
    },
    {
      icon: 'fas fa-headset',
      title: 'Quick Service Response',
      description: 'Fast breakdown support and skilled technical expertise.'
    },
    {
      icon: 'fas fa-cogs',
      title: 'Genuine Spare Parts',
      description: 'Authentic replacement parts for all machine brands.'
    },
    {
      icon: 'fas fa-handshake',
      title: 'Transparent Practices',
      description: 'Honest pricing and clear communication at every step.'
    },
    {
      icon: 'fas fa-users',
      title: 'Customer-Centric',
      description: 'Building long-term partnerships, not just transactions.'
    },
    {
      icon: 'fas fa-rupee-sign',
      title: 'Competitive Pricing',
      description: 'Best value machinery solutions in the industry.'
    }
  ];

  constructor(
    private appService: AppService,
    private toaster: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    forkJoin({
      products: this.appService.getAvailableProducts(),
      categories: this.appService.getAllCategories()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.products = data.products || [];
        this.categories = data.categories || [];
        this.isLoading = false;

        // Select first category by default if available
        if (this.categories.length > 0) {
          this.selectedCategory = this.categories[0].categoryId;
        }
        this.filterProducts();
      },
      error: (err) => {
        console.error('Error loading data', err);
        this.isLoading = false;
      }
    });
  }

  selectCategory(categoryId: number) {
    this.selectedCategory = categoryId;
    this.filterProducts();
  }

  filterProducts() {
    let baseProducts = this.products;

    if (this.selectedCategory === 0) {
      this.filteredProducts = baseProducts.slice(0, 4);
    } else {
      this.filteredProducts = baseProducts.filter(p => p.categoryId === this.selectedCategory).slice(0, 4);
    }
  }

  getProductImage(product: any): string {
    if (product.imageDetails && product.imageDetails.length > 0) {
      const primaryImg = product.imageDetails.find((img: any) => img.primary) || product.imageDetails[0];
      return this.appService.getProductImageUrl(primaryImg.imageDetailsId);
    }
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' fill='%23ccc'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' text-anchor='middle' font-size='48' fill='%23ccc'%3E%F0%9F%93%B7%3C/text%3E%3Ctext x='50%25' y='60%25' text-anchor='middle' font-size='16' fill='%23aaa' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E`;
  }

  scrollToProducts() {
    document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  // --- Enquiry Modal ---
  getEmptyEnquiry(): EnquiryRequest {
    return {
      productId: 0,
      customerName: '',
      phoneNumber: '',
      doorNo: '',
      village: '',
      street: '',
      city: '',
      state: '',
      pincode: '',
      message: ''
    };
  }

  openEnquiryModal(product: any): void {
    this.enquiryProductId = product.productId;
    this.enquiryProductName = product.productName;
    this.enquiryForm = this.getEmptyEnquiry();
    this.enquiryForm.productId = product.productId;
    this.isEnquiryModalOpen = true;
  }

  closeEnquiryModal(): void {
    this.isEnquiryModalOpen = false;
  }

  submitEnquiry(): void {
    if (!this.enquiryForm.customerName || !this.enquiryForm.phoneNumber) {
      this.toaster.warning('Please fill in your name and phone number.');
      return;
    }
    this.isSubmitting = true;
    this.appService.sendEnquiry(this.enquiryForm).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.toaster.success('Enquiry sent successfully! We will contact you soon.');
        this.closeEnquiryModal();
      },
      error: (err) => {
        console.error('Error sending enquiry', err);
        this.isSubmitting = false;
        this.toaster.error('Failed to send enquiry. Please try again.');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
